import { INTL_DAY_MONTH_NAME_SHORT } from 'constants/dateFormats'
import { intlDateFormatter } from 'lib/datetime/dateUtils'
import { useEffect, useMemo } from 'react'
import { useIntl } from 'react-intl'
import { getHotelOfferDisplayedValue } from 'lib/offer/getOfferDisplayPricing'
import { trackClientEvent } from 'analytics/analytics'
import { getCheapestPackageForDuration } from '../Common/getCheapestPackageForDuration'
import config from 'constants/config'
import { isMaldivesCharterBundleOffer } from 'constants/charterFlight'
import BaseOfferPriceContent from './BaseOfferPriceContent'

interface Props {
  duration?: number
  roomCount?: number
  offer: App.Offer | App.OfferSummary
  flightOrigin?: string
  alternativeCheckIn?: string
  alternativeCheckOut?: string
  alternativePrice?: number
  alternativeMemberPrice?: number
  alternativeValue?: number
}

const ALTERNATIVE_DATE_FORMAT = INTL_DAY_MONTH_NAME_SHORT

function HotelPriceContent({
  duration,
  roomCount,
  offer,
  flightOrigin,
  alternativeCheckIn,
  alternativeCheckOut,
  alternativePrice,
  alternativeMemberPrice,
  alternativeValue,
}: Props) {
  const intl = useIntl()
  const enableDurationSearch = !!config.LERE_ENABLE_CALENDAR_DURATION_FILTER
  const showAlternativeDates = !!(alternativeCheckIn && alternativeCheckOut && (alternativeMemberPrice || alternativePrice))

  const { flightPrices, shouldDisplayValue } = offer
  const lowestPricePackage = useMemo<App.Package | null>(() => getCheapestPackageForDuration({
    enableDurationSearch,
    offer,
    duration,
  }), [offer, duration, enableDurationSearch])

  useEffect(() => {
    if (!Number.isFinite(duration)) {
      trackClientEvent({
        action: `duration_not_finite::${duration}`,
        subject: 'HotelPriceContent',
        category: 'logging',
        type: 'operational',
      })
    }
  }, [duration])

  const flightPrice = flightOrigin ? flightPrices[flightOrigin] : undefined

  const packageCalculatedValues = useMemo(() => {
    const packageToDisplay = showAlternativeDates ? (offer.packages.find(p => p.price === alternativePrice) || lowestPricePackage) : lowestPricePackage
    if (!packageToDisplay) {
      return null
    }
    return { packageToDisplay }
  }, [
    alternativePrice,
    lowestPricePackage,
    offer.packages,
    showAlternativeDates,
  ])

  if (!packageCalculatedValues) {
    return null
  }

  const {
    packageToDisplay: {
      price,
      memberPrice,
      duration: durationToDisplay,
      value,
    },
  } = packageCalculatedValues

  const isDiscountPillHidden = offer.isDiscountPillHidden

  const captionText = showAlternativeDates ?
      intl.formatMessage(
        {
          id: '6pupcJLvgIMZm5TZATNS',
          defaultMessage: '{formattedAlternativeCheckIn} - {formattedAlternativeCheckOut} from',
          description:
          'Alternative check-in and check-out dates followed by from for the hotel price caption on a you may also like offer tile',
        },
        {
          formattedAlternativeCheckIn: intlDateFormatter(alternativeCheckIn, ALTERNATIVE_DATE_FORMAT, intl.locale),
          formattedAlternativeCheckOut: intlDateFormatter(alternativeCheckOut, ALTERNATIVE_DATE_FORMAT, intl.locale),
        },
      ) :
      intl.formatMessage(
        {
          id: 'pZBEP4O8YBHmO7LaOt6Z',
          defaultMessage: '{count, plural, one {# night} other {# nights}} from',
          description: 'Duration in nights followed by "from" for hotel price caption',
        },
        { count: durationToDisplay },
      )
  // Dated path: same LuxPlusPriceStack / roundedDollar display as Offer Page (no Math.ceil).
  const priceNumber = showAlternativeDates ? (alternativePrice ?? 0) : Math.ceil(price)
  const memberPriceNumber = showAlternativeDates ? (alternativeMemberPrice ?? 0) : Math.ceil(memberPrice)
  const valueNumber = showAlternativeDates ? (alternativeValue ?? value) : value
  const {
    highestDiscountPercent,
    shouldShowValue,
    shouldShowDiscountPercent,
  } = getHotelOfferDisplayedValue(
    !!offer.bundledWithFlightsOnly,
    memberPriceNumber || priceNumber,
    valueNumber,
    !!shouldDisplayValue,
    flightPrice,
    isMaldivesCharterBundleOffer(offer),
  )

  return <BaseOfferPriceContent
    offer={offer}
    duration={durationToDisplay}
    roomCount={showAlternativeDates ? roomCount : 1}
    captionText={captionText}
    checkIn={showAlternativeDates ? alternativeCheckIn : undefined}
    checkOut={showAlternativeDates ? alternativeCheckOut : undefined}
    memberPrice={memberPriceNumber}
    price={priceNumber}
    value={valueNumber}
    discount={shouldShowDiscountPercent && !isDiscountPillHidden ? highestDiscountPercent : undefined}
    showValueDiscount={shouldShowValue}
    saleUnit={offer.saleUnit}
  />
}

export default HotelPriceContent
